import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { Sparkles, TrendingDown, TrendingUp, Loader2, Zap, Tag, ArrowUp, Calendar, ShoppingBag, Percent, RefreshCw, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = ["segunda", "terca", "quarta", "quinta", "sexta", "sabado", "domingo"];

export default function SmartMenuPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [config, setConfig] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, analysisRes] = await Promise.all([
        authAxiosRef.current.get("/smart-menu/config"),
        authAxiosRef.current.get("/smart-menu/analysis"),
      ]);
      setConfig(cfgRes.data);
      setAnalysis(analysisRes.data);
    } catch { toast.error("Erro ao carregar dados"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authAxios.put("/smart-menu/config", config);
      setConfig(res.data);
      toast.success("Configuracoes salvas!");
    } catch { toast.error("Erro"); }
    setSaving(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await authAxios.post("/smart-menu/generate-promotions");
      toast.success(`${res.data.promotions_created} promocoes criadas! Dias fracos: ${res.data.slow_days.join(", ")}`);
      fetchData();
    } catch { toast.error("Erro ao gerar promocoes"); }
    setGenerating(false);
  };

  const handleClear = async () => {
    try {
      await authAxios.delete("/smart-menu/promotions");
      toast.success("Promocoes removidas");
      fetchData();
    } catch { toast.error("Erro"); }
  };

  if (loading) return <MainLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div></MainLayout>;

  return (
    <MainLayout>
    <div className="space-y-6 max-w-5xl" data-testid="smart-menu-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" data-testid="smart-menu-title">
            <Sparkles className="w-5 h-5 text-[#CDA34F]" /> Menu Inteligente
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Preco dinamico e reposicionamento automatico de produtos nos dias fracos.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleGenerate} disabled={generating} className="gap-2 bg-[#CDA34F] hover:bg-[#b8903e]" data-testid="generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Gerar Promocoes
          </Button>
        </div>
      </div>

      {/* Analysis: Slow Days */}
      {analysis && analysis.slow_days?.length > 0 && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="slow-days-analysis">
          <h3 className="font-bold flex items-center gap-2">
            <Calendar className="w-4 h-4 text-red-500" /> Dias Fracos (ultimos 30 dias)
          </h3>
          <p className="text-xs text-muted-foreground">Media diaria: {analysis.avg_daily_orders} pedidos | {analysis.avg_daily_revenue} EUR</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {analysis.slow_days.filter(d => d.is_slow).map((d, i) => (
              <div key={i} className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid={`slow-day-${d.day_index}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold capitalize text-gray-800">{d.day_name_pt}</span>
                  <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">-{d.pct_below_avg}%</span>
                </div>
                <p className="text-xs text-gray-500">{d.orders} pedidos (media: {d.avg_orders})</p>
                <p className="text-xs text-gray-500">{d.revenue} EUR</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis: Hidden Gems */}
      {analysis && analysis.hidden_gems?.length > 0 && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="hidden-gems">
          <h3 className="font-bold flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#CDA34F]" /> Produtos com Margem Alta e Pouca Venda
          </h3>
          <p className="text-xs text-muted-foreground">Estes produtos tem margem excelente mas vendem pouco. Ideais para promocao nos dias fracos.</p>
          <div className="space-y-2">
            {analysis.hidden_gems.slice(0, 10).map((g, i) => (
              <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3" data-testid={`gem-${i}`}>
                {g.image_url && <img src={g.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{g.name}</p>
                  <p className="text-[10px] text-muted-foreground">{g.category_name}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-emerald-600">Margem {g.margin_pct}%</p>
                  <p className="text-[10px] text-gray-500">{g.sales_count} vendas ({g.sales_pct_of_avg}% da media)</p>
                </div>
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${g.potential === "alto" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {g.potential === "alto" ? "ALTO POTENCIAL" : "MEDIO"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Data */}
      {analysis && analysis.total_orders === 0 && (
        <div className="bg-white border rounded-xl p-12 text-center">
          <ShoppingBag className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="font-bold text-gray-500">Sem dados suficientes</p>
          <p className="text-xs text-muted-foreground">Precisa de pedidos nos ultimos 30 dias para gerar a analise</p>
        </div>
      )}

      {/* Configuration */}
      {config && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="smart-menu-config">
          <h3 className="font-bold flex items-center gap-2">
            <Tag className="w-4 h-4 text-[#CDA34F]" /> Configuracao
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Desconto nos dias fracos (%)</label>
              <Input type="number" value={config.discount_pct || 10} onChange={e => setConfig(p => ({ ...p, discount_pct: parseInt(e.target.value) || 0 }))} data-testid="discount-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Max produtos promovidos</label>
              <Input type="number" value={config.max_promoted_items || 10} onChange={e => setConfig(p => ({ ...p, max_promoted_items: parseInt(e.target.value) || 5 }))} data-testid="max-items-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Margem minima para promocao (%)</label>
              <Input type="number" value={config.min_margin_pct || 40} onChange={e => setConfig(p => ({ ...p, min_margin_pct: parseInt(e.target.value) || 30 }))} data-testid="min-margin-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Texto do badge</label>
              <Input value={config.badge_text || "Oferta do Dia"} onChange={e => setConfig(p => ({ ...p, badge_text: e.target.value }))} data-testid="badge-text-input" />
            </div>
          </div>

          <div className="flex flex-wrap gap-6 pt-2">
            <div className="flex items-center gap-2">
              <Switch checked={config.auto_reposition ?? true} onCheckedChange={v => setConfig(p => ({ ...p, auto_reposition: v }))} data-testid="auto-reposition-toggle" />
              <label className="text-xs"><ArrowUp className="w-3 h-3 inline mr-1" />Subir produtos ao topo</label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={config.show_badge ?? true} onCheckedChange={v => setConfig(p => ({ ...p, show_badge: v }))} data-testid="show-badge-toggle" />
              <label className="text-xs"><Eye className="w-3 h-3 inline mr-1" />Mostrar badge de promocao</label>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#CDA34F] hover:bg-[#b8903e] gap-2" data-testid="save-config-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Salvar
            </Button>
            <Button onClick={handleClear} variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-promos-btn">
              <Trash2 className="w-4 h-4" /> Limpar Promocoes
            </Button>
          </div>
        </div>
      )}
    </div>
    </MainLayout>
  );
}
